import { translateInLanguage } from "../core/i18n.js";
import { icon } from "./icons.js";
import { demoCursor } from "./landing-tour.js";

const escape = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (ch) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        ch
      ],
  );
const tasks = [
  "Read for 20 minutes",
  "Focus on important work",
  "Learn something new",
];

export function guideExample(topic, copy, ui, language) {
  const t = (key, values) => translateInLanguage(key, language, values);
  const kind = {
    "daily-planner": "plan",
    "plan-tracker": "track",
    "daily-system": "system",
  }[topic.id];
  const taskRows = (names) =>
    names
      .map(
        (name, index) =>
          `<div class="example-task" data-example-task="${index}"><span class="example-check">${icon("check")}</span><div><span class="demo-task-name">${escape(name)}</span><small>${["08:00", "09:00", "10:00"][index]}</small></div><span class="example-task-index">0${index + 1}</span></div>`,
      )
      .join("");
  let scene;
  if (kind === "plan") {
    const names = copy.example.map(
      (text) => text.split(" · ").slice(1).join(" · ") || text,
    );
    scene = `<div class="example-scene-heading"><span>${t("Today’s plan")}</span><b data-example-count>0 / 3</b></div><div class="example-plan-input"><span data-example-typing></span><i class="example-caret"></i><b data-example-add>${icon("plus")}</b></div><div class="example-agenda">${taskRows(names)}</div><div class="example-scene-footer">${icon("check")}<span>${t("Small steps add up.")}</span><span class="example-dots"><i></i><i></i><i></i></span></div>`;
  } else if (kind === "track") {
    scene = `<div class="example-scene-heading"><span>${t("Your week, at a glance.")}</span><b>${t("This week")}</b></div><div class="example-progress"><div class="example-progress-ring"><svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="43"/><circle data-example-ring cx="50" cy="50" r="43"/></svg><b data-example-percent>0%</b></div><div><strong data-example-count>0 / 3</strong><span>${t("plans completed")}</span></div></div><div class="example-agenda">${taskRows(tasks.map((key) => t(key)))}</div><div class="example-week-bars">${["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day, index) => `<div><span><i style="--height:${[40, 65, 53, 84, 66, 92, 67][index]}%;--delay:${index * 90}ms"></i></span><small>${t(day)}</small></div>`).join("")}</div>`;
  } else {
    scene = `<div class="example-system-stages">${["Plan", "Focus", "Reflect"].map((word, index) => `<span data-example-stage="${index}">${icon(["today", "pomodoro", "weekly"][index])}${t(word)}</span>`).join("")}</div><div class="example-focus"><span class="example-focus-label">${t("FOCUS TIME")}</span><div class="example-clock"><svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="44"/><circle class="example-clock-arc" cx="50" cy="50" r="44"/></svg><b data-example-time>25:00</b></div><div class="example-focus-controls"><small>25 / 5</small><span data-example-play>${icon("play")}</span><small>${t("Current block")} 1 / 4</small></div></div><div class="example-agenda">${taskRows([t("Focus on important work")])}</div><div class="example-scene-footer">${icon("check")}<span>${t("Small steps add up.")}</span><span class="example-system-result" data-example-result>0 / 1</span></div>`;
  }
  return `<aside class="guide-example guide-example-motion" aria-labelledby="exampleTitle"><div class="example-window-bar"><span>${icon(topic.icon)} DayX</span><span class="example-window-dots" aria-hidden="true"><i></i><i></i><i></i></span></div><div class="example-title"><div class="landing-eyebrow">${escape(ui.example)}</div><h2 id="exampleTitle">${escape(topic.labels[language])}</h2></div><div class="example-scene example-scene-${kind}" data-demo="example-${kind}" aria-hidden="true">${scene}${demoCursor}</div><ol class="sr-only">${copy.example.map((text) => `<li>${escape(text)}</li>`).join("")}</ol><p class="example-note">${escape(ui.exampleNote)}</p></aside>`;
}

export function drawGuideExample(
  scene,
  settled,
  { setText, formatTime, moveCursor },
) {
  const el = scene.element;
  const seconds = (scene.elapsed % 18000) / 1000;
  const rows = [...el.querySelectorAll("[data-example-task]")];
  const kind = el.dataset.demo;
  if (kind === "example-plan") {
    const name = rows[2].querySelector(".demo-task-name").textContent;
    const added = settled || seconds >= 5;
    const done = settled ? 2 : seconds >= 11 ? 2 : seconds >= 8 ? 1 : 0;
    setText(
      el.querySelector("[data-example-typing]"),
      added
        ? name
        : name.slice(
            0,
            Math.floor(
              Math.max(0, seconds - 0.6) * Math.max(8, name.length / 3),
            ),
          ),
    );
    el.classList.toggle("example-plan-added", added);
    rows.forEach((row, index) => row.classList.toggle("done", index < done));
    setText(
      el.querySelector("[data-example-count]"),
      `${done} / ${added ? 3 : 2}`,
    );
    const cue = seconds < 6 ? 0 : seconds < 9 ? 1 : 2;
    moveCursor(
      scene,
      cue === 0
        ? el.querySelector("[data-example-add]")
        : rows[cue - 1].querySelector(".example-check"),
      cue,
      !settled && seconds >= 2 && seconds < 13,
      [5, 8, 11].some((time) => seconds >= time && seconds < time + 0.4),
    );
  } else if (kind === "example-track") {
    const done = settled ? 2 : seconds >= 6.2 ? 2 : seconds >= 3.2 ? 1 : 0;
    rows.forEach((row, index) => row.classList.toggle("done", index < done));
    setText(el.querySelector("[data-example-count]"), `${done} / 3`);
    setText(
      el.querySelector("[data-example-percent]"),
      `${Math.round((done / 3) * 100)}%`,
    );
    el.querySelector("[data-example-ring]").style.strokeDashoffset = String(
      270 * (1 - done / 3),
    );
    el.classList.toggle("example-progress-shown", settled || seconds >= 7);
    const cue = seconds < 4 ? 0 : 1;
    moveCursor(
      scene,
      rows[cue].querySelector(".example-check"),
      cue,
      !settled && seconds >= 1 && seconds < 8,
      [3.2, 6.2].some((time) => seconds >= time && seconds < time + 0.4),
    );
  } else {
    const stage = settled ? 2 : seconds < 3 ? 0 : seconds < 11 ? 1 : 2;
    const running = !settled && stage === 1;
    el.querySelectorAll("[data-example-stage]").forEach((item, index) =>
      item.classList.toggle("selected", index === stage),
    );
    setText(
      el.querySelector("[data-example-time]"),
      formatTime(
        1500 -
          (running ? Math.floor(seconds - 3) : stage === 2 && !settled ? 8 : 0),
      ),
    );
    if (scene.running !== running) {
      scene.running = running;
      el.querySelector("[data-example-play]").innerHTML = icon(
        running ? "pause" : "play",
      );
    }
    el.classList.toggle("example-session-running", running);
    const done = settled || seconds >= 12;
    rows[0].classList.toggle("done", done);
    setText(
      el.querySelector("[data-example-result]"),
      done ? "1 / 1" : "0 / 1",
    );
    moveCursor(
      scene,
      seconds < 8
        ? el.querySelector("[data-example-play]")
        : rows[0].querySelector(".example-check"),
      seconds < 8 ? 0 : 1,
      !settled && seconds >= 1 && seconds < 14,
      [3, 12].some((time) => seconds >= time && seconds < time + 0.4),
    );
  }
}
